import React, { useState, useEffect, createContext } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { getFieldById, getCropById, getCropsForField, getSoilReportsByField } from '../../services/apiService';
import { useToasts } from '../../hooks/ToastContext';

// 1. Define Steps and Context
const WIZARD_STEPS = [
  { path: '/journey/welcome', title: 'Welcome' },
  { path: '/journey/field-setup', title: 'Field Details' },
  { path: '/soil-understanding', title: 'Soil Health' },
  { path: '/journey/crop-health', title: 'Crop Details' },
  { path: '/journey/recommendation-preview', title: 'Recommendation' },
];

export const WizardContext = createContext(null);

const JourneyLayout = () => {
  const { addToast } = useToasts();
  const [searchParams] = useSearchParams();
  const fieldId = searchParams.get('fieldId');
  const cropId = searchParams.get('cropId');

  // 2. Centralized State Management
  const [wizardData, setWizardData] = useState({
    // Initial state for all steps
    name: '',
    areaValue: '',
    areaUnit: 'acre',
    village: '',
    taluka: '',
    district: '',
    state: { value: 'MH', label: 'Maharashtra' },
    latitude: null,
    longitude: null,
    boundary: null, // Add boundary to store polygon
    soilTexture: '',
    irrigationSystem: '',
    soilReportFile: null,
    existingSoilReports: [],
    cropName: '',
    variety: '',
    sowingDate: '',
    growthDuration: 0,
    cropImage: null,
    existingImages: [],
  });

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const location = useLocation();

  // Effect to preload data for editing
  useEffect(() => {
    const loadData = async () => {
      try {
        if (fieldId) {
          const [field, soilReports] = await Promise.all([
            getFieldById(fieldId),
            getSoilReportsByField(fieldId).catch(() => []) // Fallback to empty if fails
          ]);

          setWizardData(prev => ({
            ...prev,
            fieldId: field._id,
            name: field.name,
            areaValue: field.area.value,
            areaUnit: field.area.unit,
            village: { value: field.location.village, label: field.location.village },
            taluka: { value: field.location.taluka, label: field.location.taluka },
            district: { value: field.location.district, label: field.location.district },
            state: { value: field.location.state, label: field.location.state },
            latitude: field.location.latitude,
            longitude: field.location.longitude,
            boundary: field.boundary, // Populate boundary
            soilTexture: field.soilTexture,
            irrigationSystem: field.irrigationSystem,
            existingSoilReports: soilReports,
          }));

          let cropToLoad;
          if (cropId) {
            cropToLoad = await getCropById(cropId);
          } else {
            const crops = await getCropsForField(fieldId);
            cropToLoad = crops.find(c => c.isActive);
          }

          if (cropToLoad) {
            setWizardData(prev => ({
                ...prev,
                cropId: cropToLoad._id,
                cropName: cropToLoad.cropMaster?.cropName || cropToLoad.cropName,
                selectedCropId: cropToLoad.cropMaster?._id || cropToLoad.cropMaster,
                variety: cropToLoad.variety,
                sowingDate: cropToLoad.sowingDate ? new Date(cropToLoad.sowingDate).toISOString().split('T')[0] : '',
                growthDuration: cropToLoad.growthDuration,
                existingImages: cropToLoad.images || [],
                lccScore: cropToLoad.sugarcaneMetrics?.lccScore || prev.lccScore,
                caneHeight: cropToLoad.sugarcaneMetrics?.caneHeight || prev.caneHeight,
                caneGirth: cropToLoad.sugarcaneMetrics?.caneGirth || prev.caneGirth,
            }));
          }
        }
      } catch (error) {
        console.error('Error loading data for wizard:', error);
        addToast('Failed to load data for editing.', 'error');
      }
    };
    loadData();
  }, [fieldId, cropId, addToast]);


  // 3. Sync Step with URL
  useEffect(() => {
    const currentPath = location.pathname;
    const stepIndex = WIZARD_STEPS.findIndex(step => currentPath.startsWith(step.path));
    
    if (stepIndex !== -1) {
      setCurrentStepIndex(stepIndex);
    }
  }, [location.pathname]);

  const totalSteps = WIZARD_STEPS.length;
  const progressPercentage = totalSteps > 1 ? ((currentStepIndex + 1) / totalSteps) * 100 : 100;

  return (
    <WizardContext.Provider value={{ wizardData, setWizardData, currentStepIndex, totalSteps }}>
      <div>
        {/* 4. Dynamic Stepper / Progress Bar */}
        <div 
          className="p-4 backdrop-blur-md shadow-lg border-b border-divider-main sticky top-16 z-30"
          style={{ background: 'var(--bg-surface-translucent)' }}
        >
          <div className="max-w-4xl mx-auto">
            <p className="text-center text-sm font-bold text-main mb-2">
              {`Step ${currentStepIndex + 1} of ${totalSteps}: ${WIZARD_STEPS[currentStepIndex]?.title || ''}`}
            </p>
            <div className="h-2 bg-divider-main rounded-full overflow-hidden">
              <div 
                className="h-2 bg-primary-500 rounded-full transition-all duration-500 ease-in-out shadow-[0_0_10px_rgba(22,163,74,0.4)]" 
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <main 
          className="max-w-4xl mx-auto p-4 mt-8 backdrop-blur-md shadow-2xl border border-divider-main rounded-2xl"
          style={{ background: 'var(--bg-surface-translucent)' }}
        >
          <Outlet />
        </main>
      </div>
    </WizardContext.Provider>
  );
};

export default JourneyLayout;